<template>
    <ModeSwitcher
        ref="modeSwitcher"
        :main-mode-text="$t('create')"
        :create-mode-text="$t('product.info.create_timetable')"
        :edit-mode-text="$t('product.info.edit_timetable')"
        @reset="ediServiceId = null"
    >
        <template v-slot:btn-block>
            <div class="full-width d-flex align-center">
                <v-spacer />
                <FilterStatusToggle v-model="activeFilter" />
            </div>
        </template>
        <template v-slot:main-mode>
            <v-data-table
                :headers="useHeaders"
                :items="useFilteredServices"
                :items-per-page="-1"
                :items-per-page-options="[-1]"
                :loading="isLoading"
                :items-length="useFilteredServices.length"
                :custom-key-sort="{
                    name: (a, b) => localized(a).localeCompare(localized(b)),
                }"
                :sort-by="[{key: 'name', order: 'asc'}]"
                must-sort
                density="compact"
                color="primary"
                class="elevation-1 table-server"
                data-cy="guests-table"
            >
                <template v-slot:item.name="{item}">
                    <div class="text-link" @click="ediService(item.id)">
                        {{ localized(item.name) }}
                    </div>
                </template>
                <template v-slot:item.active="{item}">
                    <p>{{ item.active ? $t('active') : $t('inactive') }}</p>
                </template>
            </v-data-table>
        </template>
        <template v-slot:additional-mode>
            <InfoTransferServiceForm
                :edi-service-id="ediServiceId"
                :contract-id="contractId"
                :catalogue="useCatalogue"
                @save="save()"
                @cancel="modeSwitcher.goToTable()"
            />
        </template>
    </ModeSwitcher>
</template>

<script setup lang="ts">
    import InfoTransferServiceForm from '~/components/products/information/InfoTransferServiceForm.vue'
    import {useCatalogue} from '~/composables/form-helpers'

    const localized = useLocalized()

    type HeaderItemType = {
        title: string
        value: string
        width?: string
        minWidth?: string
    }

    const props = defineProps<{
        contractId: number
    }>()

    const isLoading = ref<boolean>(false)
    const transferServices = ref<any[]>([])
    const modeSwitcher = ref(null)
    const ediServiceId = ref<number>(null)
    const activeFilter = ref<boolean>(true)

    const app = useNuxtApp()
    const {t} = useI18n()

    onMounted(async () => {
        await loadData()
        if (!useCatalogue.value?.length) await supplierStore.loadCatalogue()
    })

    async function loadData(): Promise<void> {
        try {
            isLoading.value = true
            const rq = {contractId: props.contractId}
            const {services} = await app.$api.ownTransferServices.get(rq)
            transferServices.value = services
        } catch (e) {
            transferServices.value = []
        } finally {
            isLoading.value = false
        }
    }

    const save = async (): Promise<void> => {
        await loadData()
        modeSwitcher.value.goToTable()
    }

    const useHeaders: ComputedRef<HeaderItemType[]> = computed(() => {
        const headers: HeaderItemType[] = [
            {title: t('name'), value: 'name', minWidth: '120px', sortable: true},
            {title: t('transport'), value: 'transport'},
            {title: t('driver_language'), value: 'driverLanguages'},
            {title: t('min_max_occupancy'), value: 'occupancy'},
            {title: t('status'), value: 'active', width: '140px', minWidth: '140px'},
        ]
        return headers
    })

    const useItems = computed(() => {
        return transferServices.value.map(
            ({
                id,
                name,
                startTime,
                active,
                categoryId,
                ownTransferServiceDriverDescriptions,
                maxOccupancy,
                minOccupancy,
            }) => ({
                id,
                name,
                startTime,
                active,
                transport: useCatalogue.value.find(({id}) => id == categoryId)?.name || '',
                driverLanguages: ownTransferServiceDriverDescriptions.map(({languageCode}) => languageCode).join(', '),
                occupancy: `${minOccupancy || ''}${minOccupancy && maxOccupancy ? '-' : ''}${maxOccupancy || ''}`,
            })
        )
    })

    const ediService = (id: number): void => {
        modeSwitcher.value.edit()
        ediServiceId.value = id
    }

    const useFilteredServices = computed(() => {
        return useItems.value.filter(({active}) => activeFilter.value === active)
    })
</script>
